import Excel from 'exceljs';
import { ClaimRow } from '../../../api/endpoints';
import { formatDate, US_DATE_FORMAT } from '../../../utils/formatDate';
import { StructureFooter } from '../typesExhibitExporter';
import { getClaimNumber } from '../utils/field';
import { IExcelCreator } from './excelExporter';
import { ExhibitTemplateProps } from './exhibitType';
import {
  createTotalRowStyle,
  defCellStyle,
  defPositionNumberStyle,
} from './utils/excelConst';
import {
  createExcelWorkbook,
  ExcelDocumentHeader,
} from './utils/excelCreateWorkbook';
import { excelAddPageHeader, HeaderPositionCell } from './utils/excelHeader';
import {
  deleteObjectionCommentaryColumn,
  excelAddPageFooter,
  excelAddRow,
  excelAddTableHeaderRow,
  excelAddTotalRow,
} from './utils/excelUtils';
import {
  createClaimAmount,
  createHeaderFooter,
  getAddressLine,
  getTotalCurrency,
} from './utils/exporterUtils';
import { IExhibitWorkbookData, IRowValues, TRowStyle } from './utils/type';

const COLUMN_LIST: ExcelDocumentHeader[] = [
  { header: '', width: 4 },
  { header: 'Original Creditor', width: 20 },
  { header: 'Claim transferred to', width: 12 },
  {
    header: 'Claim/schedule to be disallowed and expunged',
    width: 13.5,
  },
  { header: 'Debtor', width: 25 },
  { header: 'Date filed/scheduled', width: 11.5 },
  { header: 'Claim amount and priority (1)', width: 14.9 },
  { header: 'Basis for objection', width: 17.6 },
  { header: 'Comments', width: 15 },
];

const valuesStyle: TRowStyle = [
  { ...defPositionNumberStyle }, //'positionNumber',
  { ...defCellStyle }, //'originalCreditor',
  { ...defCellStyle }, //'claimTransferredTo',
  {
    ...defCellStyle,
    alignment: { horizontal: 'center', vertical: 'top' },
  }, //'referenceNumber',
  { ...defCellStyle }, //'proposedDebtor',
  {
    ...defCellStyle,
    numFmt: 'mm-dd-yy',
    alignment: { horizontal: 'center', vertical: 'top' },
  }, //'officialClaimDate',
  {
    ...defCellStyle,
    numFmt: '[$$-409]#,##0.00',
    alignment: { horizontal: 'right', vertical: 'top', wrapText: true },
  }, //'claimAmount',
  { ...defCellStyle }, //'objectionBasis',
  { ...defCellStyle }, //'objectionCommentary',
];

const valuesTotalStyle = createTotalRowStyle(valuesStyle.length);

const objectionColId = 9;

const PAGE_HEADER_POS: HeaderPositionCell = {
  left: 1,
  middle: 4,
  right: 7,
  length: 9,
};

export const templateAllowedClaims: IExcelCreator = {
  fileName: 'Allowed_Claims.xlsx',
  getWorkbook: async (props: ExhibitTemplateProps) => {
    const { displayObjection } = props.exhibit.structure;
    const excelData = getDataToShow(props);
    return await createWorkbook(excelData, displayObjection);
  },
};

async function createWorkbook(
  data: IExhibitWorkbookData,
  displayObjection: boolean | undefined,
): Promise<Excel.Workbook> {
  const workbook = createExcelWorkbook('Sheet1', COLUMN_LIST);
  const firstSheet = workbook.getWorksheet(1);

  excelAddPageHeader(firstSheet, data.headerFooter.header, PAGE_HEADER_POS);
  excelAddTableHeaderRow(firstSheet, COLUMN_LIST);
  excelAddPageFooter(firstSheet, data.headerFooter);

  data.claimsList.forEach((row) => excelAddRow(firstSheet, row, valuesStyle));
  excelAddTotalRow(firstSheet, data.totalRow, valuesTotalStyle);

  deleteObjectionCommentaryColumn(firstSheet, displayObjection, objectionColId);
  return workbook;
}

export function getDataToShow(props: ExhibitTemplateProps): IExhibitWorkbookData {
  const { exhibit, claimItems } = props;
  const { footer } = exhibit.structure;

  const showClaimCount = footer.includes(StructureFooter.ClaimCount);

  return {
    headerFooter: createHeaderFooter(props),
    claimsList: getPreparedClaimsData(
      claimItems,
      exhibit.structure.displayObjection,
      exhibit.objectionBasis,
    ),
    totalRow: getTotalRow(claimItems, showClaimCount),
  };
}

function getTotalRow(claims: ClaimRow[], showClaimCount: boolean): IRowValues {
  const values = [
    '', //positionNumber
    'Totals', //originalCreditor
    '', //claimTransferredTo
    showClaimCount ? `Count: ${claims.length}` : '', //referenceNumber
    '', //proposedDebtor
    '', //officialClaimDate
    getTotalCurrency(claims, 'currentTotal'), //claimAmount
    '', //objectionBasis
  ];

  return { values };
}

const getPreparedClaimsData = (
  claims: ClaimRow[],
  displayObjection: boolean | undefined,
  objectionBasis: string,
) => {
  return claims.map(
    (c, index) =>
      ({
        values: [
          index + 1, //positionNumber
          getAddressLine(c, c.redactNameAddress), //originalCreditor
          '', //claimTransferredTo
          getClaimNumber(c), //referenceNumber
          c.proposedDebtor, //proposedDebtor
          formatDate(c.officialClaimDate, US_DATE_FORMAT), //officialClaimDate
          createClaimAmount(c), //claimAmount
          objectionBasis, //objectionBasis
          displayObjection ? c.objectionCommentary : '', //objectionCommentary
        ],
      } as IRowValues),
  );
};
